import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AttachFile as AttachFileIcon,
  DeleteOutline as DeleteIcon,
  Image as ImageIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import axios from '../../config/axios';
import { getApiUrl } from '../../config/environment';
import PatientDocumentThumbnail from '../clinic/PatientDocumentThumbnail';
import { useClinicalImageAttachFlow } from '../common/ClinicalImageAttach';
import {
  evolutionCaptionMutedSx,
  evolutionEmptyStateSx,
  evolutionSectionHeadingSx,
  evolutionSectionPaperSxFor,
  evolutionSectionBodySx,
  evolutionSecondaryOutlinedButtonSx,
} from './evolutionTheme';

type EvolutionFileAttachmentsSectionProps = {
  patientId: string;
  evolutionId: string | null;
  disabled?: boolean;
  professionalId?: string;
  onEnsureEvolutionId: () => Promise<string | null>;
  onNotify?: (message: string, severity?: 'success' | 'error' | 'warning') => void;
  compact?: boolean;
};

function defaultTitleFromFile(file: File): string {
  const base = file.name.replace(/\.[^/.]+$/, '').trim();
  return base || file.name;
}

export default function EvolutionFileAttachmentsSection({
  patientId,
  evolutionId,
  disabled = false,
  professionalId,
  onEnsureEvolutionId,
  onNotify,
  compact = false,
}: EvolutionFileAttachmentsSectionProps) {
  const [documents, setDocuments] = useState<any[]>([]);
  const [images, setImages] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(!compact);

  const docInputRef = useRef<HTMLInputElement>(null);
  const onNotifyRef = useRef(onNotify);
  onNotifyRef.current = onNotify;
  const uploadImageFilesRef = useRef<(files: File[]) => Promise<void>>(async () => {});

  const loadAttachments = useCallback(async () => {
    if (!evolutionId) {
      setDocuments([]);
      setImages([]);
      return;
    }
    setLoading(true);
    try {
      const { data: res } = await axios.get(getApiUrl(`/clinic/evolutions/${evolutionId}/file-attachments`));
      const payload = res?.data || {};
      setDocuments(Array.isArray(payload.documents) ? payload.documents : []);
      setImages(Array.isArray(payload.images) ? payload.images : []);
    } catch {
      onNotifyRef.current?.('Não foi possível carregar os anexos da evolução.', 'error');
    } finally {
      setLoading(false);
    }
  }, [evolutionId]);

  useEffect(() => {
    void loadAttachments();
  }, [loadAttachments]);

  const { openImageSourceChooser, imageSourcePickerNodes } = useClinicalImageAttachFlow({
    onFiles: (files) => {
      void uploadImageFilesRef.current(files);
    },
  });

  const resolveEvolutionIdForUpload = async (): Promise<string | null> => {
    if (evolutionId) return evolutionId;
    const id = await onEnsureEvolutionId();
    if (!id) {
      onNotify?.(
        'Salve um rascunho da evolução antes de anexar (texto, impresso ou indicação).',
        'warning',
      );
      return null;
    }
    return id;
  };

  const uploadDocumentFiles = async (files: File[]) => {
    if (!patientId || files.length === 0) return;
    const eid = await resolveEvolutionIdForUpload();
    if (!eid) return;

    setUploading(true);
    const uploaded: any[] = [];
    let failed = 0;
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('title', defaultTitleFromFile(file));
        fd.append('document_type', file.type.startsWith('image/') ? 'photo' : 'other');
        fd.append('evolution_id', eid);
        try {
          const { data: res } = await axios.post(
            getApiUrl(`/clinic/patients/${patientId}/documents/upload`),
            fd,
            { headers: { 'Content-Type': 'multipart/form-data' } },
          );
          if (res?.success && res.data) uploaded.push(res.data);
          else failed += 1;
        } catch {
          failed += 1;
        }
      }
      if (uploaded.length) {
        setDocuments((prev) => [...uploaded, ...prev]);
        onNotify?.(
          uploaded.length === 1 ? 'Documento anexado à evolução.' : `${uploaded.length} documentos anexados.`,
          'success',
        );
      }
      if (failed > 0) {
        onNotify?.(`${failed} arquivo(s) não puderam ser enviados.`, 'error');
      }
      if (uploaded.length && eid !== evolutionId) {
        await loadAttachments();
      }
    } finally {
      setUploading(false);
    }
  };

  const uploadImageFiles = async (files: File[]) => {
    if (!patientId || files.length === 0) return;
    const eid = await resolveEvolutionIdForUpload();
    if (!eid) return;

    setUploading(true);
    const uploaded: any[] = [];
    let failed = 0;
    try {
      for (const file of files) {
        try {
          const fd = new FormData();
          fd.append('file', file);
          const { data: uploadRes } = await axios.post(getApiUrl('/clinic/patient-images/upload'), fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          if (!uploadRes?.success || !uploadRes.data?.file_url) {
            failed += 1;
            continue;
          }
          const { data: createRes } = await axios.post(getApiUrl('/clinic/patient-images'), {
            patient_id: patientId,
            professional_id: professionalId || null,
            evolution_id: eid,
            title: defaultTitleFromFile(file),
            image_type: 'clinical_photo',
            file_url: uploadRes.data.file_url,
            file_name: uploadRes.data.file_name || file.name,
            file_type: uploadRes.data.file_type || file.type,
          });
          if (createRes?.success && createRes.data) uploaded.push(createRes.data);
          else failed += 1;
        } catch {
          failed += 1;
        }
      }
      if (uploaded.length) {
        setImages((prev) => [...uploaded, ...prev]);
        onNotify?.(
          uploaded.length === 1 ? 'Imagem anexada à evolução.' : `${uploaded.length} imagens anexadas.`,
          'success',
        );
      }
      if (failed > 0) {
        onNotify?.(`${failed} imagem(ns) não puderam ser enviadas.`, 'error');
      }
      if (uploaded.length && eid !== evolutionId) {
        await loadAttachments();
      }
    } finally {
      setUploading(false);
    }
  };

  uploadImageFilesRef.current = uploadImageFiles;

  const handleDeleteDocument = async (docId: string) => {
    if (!window.confirm('Remover este documento da evolução e do prontuário?')) return;
    try {
      await axios.delete(getApiUrl(`/clinic/patients/${patientId}/documents/${docId}`));
      setDocuments((prev) => prev.filter((d) => d.id !== docId));
      onNotify?.('Documento removido.', 'success');
    } catch {
      onNotify?.('Erro ao remover documento.', 'error');
    }
  };

  const handleDeleteImage = async (imageId: string) => {
    if (!window.confirm('Remover esta imagem da evolução e do prontuário?')) return;
    try {
      await axios.delete(getApiUrl(`/clinic/patient-images/${imageId}`));
      setImages((prev) => prev.filter((i) => i.id !== imageId));
      onNotify?.('Imagem removida.', 'success');
    } catch {
      onNotify?.('Erro ao remover imagem.', 'error');
    }
  };

  const total = documents.length + images.length;
  const sectionDisabled = disabled || uploading;

  useEffect(() => {
    if (!compact) setDetailsOpen(true);
  }, [compact]);

  useEffect(() => {
    if (total > 0) setDetailsOpen(true);
  }, [total]);

  const headerCaption = 'Imagens e documentos ficam no prontuário do paciente e vinculados a esta evolução.';

  return (
    <Paper elevation={0} sx={evolutionSectionPaperSxFor(compact)}>
      <Box
        sx={{
          ...evolutionSectionBodySx(compact),
          pt: compact ? 0.75 : 2,
          pb: compact ? 0.5 : 1,
          display: 'flex',
          justifyContent: 'space-between',
          gap: 1,
          flexWrap: 'wrap',
          alignItems: 'flex-start',
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ ...evolutionSectionHeadingSx, fontSize: compact ? '0.72rem' : undefined }}>
            ANEXOS (ARQUIVOS)
            {compact && total > 0 ? (
              <Typography component="span" variant="caption" sx={{ ml: 0.75, fontWeight: 600, color: '#64748b' }}>
                ({total})
              </Typography>
            ) : null}
          </Typography>
          {!compact ? (
            <Typography variant="caption" sx={evolutionCaptionMutedSx}>
              {headerCaption}
            </Typography>
          ) : (
            <Tooltip title={headerCaption} placement="top">
              <Typography variant="caption" sx={{ ...evolutionCaptionMutedSx, display: 'block', mt: 0.15 }}>
                Prontuário · vinculados à evolução
              </Typography>
            </Tooltip>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <input
            ref={docInputRef}
            type="file"
            hidden
            multiple
            accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,image/*"
            onChange={(e) => {
              const files = Array.from(e.target.files || []);
              e.target.value = '';
              if (files.length) void uploadDocumentFiles(files);
            }}
          />
          {imageSourcePickerNodes}
          <Button
            size="small"
            variant="outlined"
            startIcon={uploading ? <CircularProgress size={14} /> : <AttachFileIcon />}
            disabled={sectionDisabled}
            onClick={() => docInputRef.current?.click()}
            sx={evolutionSecondaryOutlinedButtonSx}
          >
            Documento
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={uploading ? <CircularProgress size={14} /> : <ImageIcon />}
            disabled={sectionDisabled}
            onClick={openImageSourceChooser}
            sx={evolutionSecondaryOutlinedButtonSx}
          >
            Imagem
          </Button>
          {compact && total === 0 && (
            <Button
              size="small"
              onClick={() => setDetailsOpen((v) => !v)}
              sx={{ textTransform: 'none', fontWeight: 600, alignSelf: 'center' }}
            >
              {detailsOpen ? 'Menos' : 'Detalhes'}
            </Button>
          )}
        </Box>
      </Box>

      <Collapse in={!compact || detailsOpen || total > 0} unmountOnExit={compact}>
      <Box sx={{ ...evolutionSectionBodySx(compact), pt: 0 }}>
        {!evolutionId && (
          <Alert severity="info" sx={{ mb: compact ? 0.75 : 1.5, py: compact ? 0 : undefined, fontSize: '0.78rem' }}>
            {compact
              ? 'Rascunho criado ao anexar o 1º arquivo (paciente, profissional e conteúdo clínico).'
              : 'O rascunho da evolução será criado automaticamente ao anexar o primeiro arquivo (é preciso ter paciente, profissional e algum conteúdo clínico ou impresso).'}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: compact ? 1.5 : 3 }}>
            <CircularProgress size={compact ? 22 : 28} />
          </Box>
        ) : total === 0 ? (
          <Typography variant="body2" sx={{ ...evolutionEmptyStateSx, fontSize: compact ? '0.8rem' : undefined }}>
            Nenhum arquivo anexado nesta evolução.
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {documents.map((doc) => (
              <Box
                key={doc.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  p: 1,
                  borderRadius: 1.5,
                  border: '1px solid #e2e8f0',
                  bgcolor: '#fafafa',
                }}
              >
                <PatientDocumentThumbnail doc={doc} size={44} accentColor="#0891b2" />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {doc.title || doc.file_name || 'Documento'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">Documento</Typography>
                </Box>
                <Tooltip title="Abrir">
                  <IconButton size="small" href={doc.file_url} target="_blank" rel="noopener noreferrer">
                    <OpenInNewIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                {!disabled && (
                  <Tooltip title="Remover">
                    <IconButton size="small" onClick={() => void handleDeleteDocument(doc.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            ))}
            {images.map((img) => (
              <Box
                key={img.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  p: 1,
                  borderRadius: 1.5,
                  border: '1px solid #e2e8f0',
                  bgcolor: '#fafafa',
                }}
              >
                <Box
                  component="img"
                  src={img.file_url}
                  alt={img.title || ''}
                  sx={{ width: 44, height: 44, borderRadius: 1.5, objectFit: 'cover', flexShrink: 0, bgcolor: '#e2e8f0' }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {img.title || img.file_name || 'Imagem'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.35 }}>
                    <ImageIcon sx={{ fontSize: 14 }} /> Imagem clínica
                  </Typography>
                </Box>
                <Tooltip title="Abrir">
                  <IconButton size="small" href={img.file_url} target="_blank" rel="noopener noreferrer">
                    <OpenInNewIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                {!disabled && (
                  <Tooltip title="Remover">
                    <IconButton size="small" onClick={() => void handleDeleteImage(img.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            ))}
          </Box>
        )}
      </Box>
      </Collapse>
    </Paper>
  );
}
